import { useMemo, useState } from "react";

import { useAuditLog, type AuditQueryArgs } from "../../api/queries.js";
import { ErrorAlert } from "../../components/ErrorAlert.js";
import { Skeleton } from "../../components/Skeleton.js";
import { StatusBadge } from "../../components/StatusBadge.js";

const PAGE_SIZE = 50;

// The actions our backend records. Kept in sync with apps/api/src/audit/actions.ts.
const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "connection.created", label: "Connection created" },
  { value: "connection.updated", label: "Connection updated" },
  { value: "connection.deleted", label: "Connection deleted" },
  { value: "connection.tested", label: "Connection tested" },
  { value: "schedule.created", label: "Schedule created" },
  { value: "schedule.updated", label: "Schedule updated" },
  { value: "schedule.deleted", label: "Schedule deleted" },
  { value: "schedule.run_now", label: "Schedule run now" },
  { value: "schedule.resumed", label: "Schedule resumed" },
  { value: "schedule.suspended", label: "Schedule auto-suspended" },
  { value: "reload.immediate", label: "Reload triggered" },
  { value: "reload.cancelled", label: "Reload cancelled" },
] as const;

// Verb-only colouring; tone-mapped so destructive + system actions stand out.
// Tone names match StatusBadge's Tone type (green/red/yellow/blue/grey/slate).
function actionTone(
  action: string,
): "green" | "red" | "yellow" | "blue" | "grey" {
  if (action.endsWith(".deleted") || action.endsWith(".cancelled")) return "red";
  if (action.endsWith(".suspended")) return "yellow";
  if (action.endsWith(".created") || action.endsWith(".resumed")) return "green";
  if (action.endsWith(".run_now") || action.endsWith(".immediate")) return "blue";
  return "grey";
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function ActivityTab() {
  const [action, setAction] = useState("");
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const args: AuditQueryArgs = useMemo(
    () => ({ action: action || undefined, limit: PAGE_SIZE, offset }),
    [action, offset],
  );
  const q = useAuditLog(args, { refetchInterval: 10_000 });

  function toggle(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const total = q.data?.total ?? 0;
  const rows = q.data?.data ?? [];
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + rows.length, total);

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="field-label" htmlFor="action">
              Action
            </label>
            <select
              id="action"
              className="input min-w-[220px]"
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setOffset(0);
              }}
            >
              {ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="ml-auto text-[12px] text-ink-soft">
            {total === 0 ? "No events" : `${pageStart}–${pageEnd} of ${total}`}
          </div>
        </div>
      </div>

      {q.isError ? (
        <ErrorAlert>
          {q.error instanceof Error ? q.error.message : "Failed to load activity"}
        </ErrorAlert>
      ) : null}

      {q.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={56} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="empty-surface">
            <p>No events recorded yet. Take an action and it will appear here.</p>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head text-left">When</th>
                <th className="table-head text-left">Actor</th>
                <th className="table-head text-left">Action</th>
                <th className="table-head text-left">Target</th>
                <th className="table-head text-left" aria-label="Expand"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = expanded.has(row.id);
                return (
                  <tr
                    key={row.id}
                    className="border-b border-line align-top hover:bg-bg"
                  >
                    <td colSpan={5} className="p-0">
                      <button
                        type="button"
                        onClick={() => toggle(row.id)}
                        className="grid w-full grid-cols-[180px_220px_220px_1fr_24px] items-center gap-3 px-4 py-3 text-left"
                        aria-expanded={isOpen}
                      >
                        <span className="text-[12px] text-ink-soft">
                          {formatTime(row.occurredAt)}
                        </span>
                        <span className="text-sm text-ink">
                          {row.actorName ?? row.actorLabel}
                        </span>
                        <StatusBadge tone={actionTone(row.action)}>
                          {row.action}
                        </StatusBadge>
                        <span className="truncate text-sm text-ink-soft">
                          {row.targetType}
                          {row.targetId ? ` · ${row.targetId}` : ""}
                        </span>
                        <span
                          aria-hidden
                          className={`text-ink-soft transition ${isOpen ? "rotate-90" : ""}`}
                        >
                          ›
                        </span>
                      </button>
                      {isOpen ? (
                        <div className="border-t border-line bg-bg/50 px-4 py-3">
                          <dl className="grid grid-cols-1 gap-3 text-[12px] sm:grid-cols-2">
                            {row.before != null ? (
                              <div>
                                <dt className="field-label mb-1">Before</dt>
                                <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-card border border-line bg-white p-3 text-[12px] text-ink">
                                  {JSON.stringify(row.before, null, 2)}
                                </pre>
                              </div>
                            ) : null}
                            {row.after != null ? (
                              <div>
                                <dt className="field-label mb-1">After</dt>
                                <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-card border border-line bg-white p-3 text-[12px] text-ink">
                                  {JSON.stringify(row.after, null, 2)}
                                </pre>
                              </div>
                            ) : null}
                            {row.ip ? (
                              <div>
                                <dt className="field-label mb-1">IP</dt>
                                <code className="text-[12px] text-ink-soft">
                                  {row.ip}
                                </code>
                              </div>
                            ) : null}
                            {row.userAgent ? (
                              <div>
                                <dt className="field-label mb-1">User agent</dt>
                                <code className="break-all text-[12px] text-ink-soft">
                                  {row.userAgent}
                                </code>
                              </div>
                            ) : null}
                          </dl>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost-sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Prev
          </button>
          <button
            type="button"
            className="btn-ghost-sm"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
